
import React, { useState } from 'react';
import { ShoppingCart, X, Calendar, AlertCircle, TestTube, ChevronDown, ChevronUp } from 'lucide-react';
import { CartItem } from '@/types';
import { useTestingMode, getEffectiveMinimumAmount } from '@/contexts/TestingModeContext';

interface CartProps {
  items: CartItem[];
  total: number;
  onRemoveItem: (id: string) => void;
  onBook: () => void;
  highlightedItemId?: string | null;
}

export const Cart: React.FC<CartProps> = ({ items, total, onRemoveItem, onBook, highlightedItemId }) => {
  const { isTestingMode } = useTestingMode();
  const MINIMUM_BOOKING_AMOUNT = getEffectiveMinimumAmount(isTestingMode);
  const isMinimumMet = total >= MINIMUM_BOOKING_AMOUNT;
  const amountNeeded = MINIMUM_BOOKING_AMOUNT - total;
  const [mobileExpanded, setMobileExpanded] = useState(false);

  const lineItems = (
    <div className="space-y-2 max-h-40 overflow-y-auto">
      {items.map((item) => (
        <div
          key={item.id}
          className={`flex items-center justify-between bg-slate-800 rounded-lg px-3 py-2 transition-all duration-500 ${
            highlightedItemId === item.id
              ? 'bg-blue-900/50 border border-blue-500/50 animate-pulse'
              : 'hover:bg-slate-700'
          }`}
        >
          <div className="flex-1 min-w-0 pr-2">
            <div className="text-white text-sm font-medium truncate">{item.name}</div>
            <div className="text-xs text-slate-400">
              ${item.price} x {item.quantity}
            </div>
            {item.options && (
              <div className="text-[11px] text-slate-500 truncate">
                {item.options.over65 && 'Over 65", '}
                {item.options.frameMount && 'Frame Mount, '}
                {item.options.numberOfTvs > 1 && `${item.options.numberOfTvs} TVs, `}
                {item.options.wallType !== 'standard' && `${item.options.wallType} Wall`}
              </div>
            )}
          </div>
          <button
            onClick={() => onRemoveItem(item.id)}
            className="shrink-0 text-slate-400 hover:text-red-400 p-1 transition-colors duration-200"
            aria-label="Remove item"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );

  const minimumWarning = !isMinimumMet && items.length > 0 && (
    <div className="p-3 bg-orange-900/30 border border-orange-500/50 rounded-lg">
      <div className="flex items-center space-x-2 mb-1">
        <AlertCircle className="h-4 w-4 text-orange-400" />
        <span className="text-sm font-medium text-orange-300">Minimum Required</span>
      </div>
      <p className="text-xs text-orange-200">
        Add ${amountNeeded} more to reach the ${MINIMUM_BOOKING_AMOUNT} minimum.
      </p>
    </div>
  );

  return (
    <>
      {/* Desktop cart (unchanged) */}
      <div className="hidden md:block fixed bottom-4 right-4 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl max-w-sm w-full z-40 animate-fade-in">
        <div className="p-4">
          <div className="flex items-center space-x-3 mb-4">
            <ShoppingCart className="h-5 w-5 text-blue-400" />
            <h3 className="text-lg font-semibold text-white">Cart ({items.length})</h3>
            {isTestingMode && (
              <div className="flex items-center space-x-1 bg-green-900/30 border border-green-500/50 px-2 py-1 rounded text-xs text-green-300">
                <TestTube className="h-3 w-3" />
                <span>Testing Mode</span>
              </div>
            )}
          </div>

          {lineItems}

          <div className="mt-4 pt-4 border-t border-slate-700">
            <div className="flex justify-between items-center mb-4">
              <span className="text-lg font-semibold text-white">Total:</span>
              <span className="text-xl font-bold text-blue-400 transition-all duration-500 transform">
                ${total}
              </span>
            </div>

            {minimumWarning && <div className="mb-4">{minimumWarning}</div>}

            <button
              onClick={onBook}
              disabled={!isMinimumMet}
              className={`w-full font-semibold py-3 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 ${
                isMinimumMet
                  ? 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg'
                  : 'bg-slate-600 text-slate-400 cursor-not-allowed opacity-50'
              }`}
            >
              <Calendar className="h-4 w-4" />
              <span>{isMinimumMet ? 'Book Service' : `Add $${amountNeeded} to Book`}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile sticky bottom bar */}
      <div className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-slate-900 border-t border-slate-700 shadow-2xl animate-fade-in pb-[env(safe-area-inset-bottom)]">
        {mobileExpanded && (
          <div className="px-3 pt-3 space-y-2 border-b border-slate-800">
            {lineItems}
            {minimumWarning}
          </div>
        )}
        <div className="px-3 py-2 flex items-center gap-3">
          <button
            onClick={() => setMobileExpanded((v) => !v)}
            className="flex items-center gap-2 min-w-0 flex-1 text-left"
            aria-expanded={mobileExpanded}
          >
            <ShoppingCart className="h-5 w-5 text-blue-400 shrink-0" />
            <div className="min-w-0">
              <div className="text-white text-sm font-semibold truncate">
                Cart ({items.length})
              </div>
              <div className="text-xs text-slate-400 truncate">
                Total: <span className="text-blue-400 font-semibold">${total}</span>
              </div>
            </div>
            {mobileExpanded ? (
              <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
            ) : (
              <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" />
            )}
          </button>
          <button
            onClick={onBook}
            disabled={!isMinimumMet}
            className={`shrink-0 font-semibold px-4 h-11 rounded-lg text-sm transition-all flex items-center gap-2 ${
              isMinimumMet
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-slate-700 text-slate-400 cursor-not-allowed'
            }`}
          >
            <Calendar className="h-4 w-4" />
            <span>{isMinimumMet ? 'Book Service' : `+$${amountNeeded}`}</span>
          </button>
        </div>
      </div>
    </>
  );
};
